import React, { FunctionComponent } from 'react';
import { Redirect, Route, RouteProps, useLocation } from 'react-router-dom';

import useTypedSelector from 'hooks/useTypedSelector';
import {
  isUserLoggedInSelector,
  userRolesSelector,
  isUserVerifiedSelector,
} from 'redux/selectors/auth.selector';
import { systemStatusSelector } from 'redux/selectors/systemStatus.selector';
import MaintenancePage from 'components/commons/MaintenancePage';
import { getCookie } from 'helpers/cookies';
import { ROLES } from 'helpers/roles';
import { CircularProgress } from '@mui/material';
import { FullPageLoader } from 'styles/view';

interface IRouteContainerProps extends RouteProps {
  authenticated?: boolean;
  isPublicRoute?: boolean;
  restricted?: boolean;
  verified?: boolean;
  roles?: Array<string>;
  excludeMaintenance?: boolean;
}

const RouteContainer: FunctionComponent<IRouteContainerProps> = ({
  component: Component,
  authenticated = false,
  isPublicRoute = false,
  restricted = false,
  verified = false,
  roles = [],
  excludeMaintenance = false,
  ...rest
}) => {
  const location = useLocation();
  const isUserLoggedIn = useTypedSelector(isUserLoggedInSelector);
  const userRoles = useTypedSelector(userRolesSelector);
  const isUserVerified = useTypedSelector(isUserVerifiedSelector);
  const isInitializing = useTypedSelector((state) => state.auth.initializing);
  const isAdmin = userRoles?.includes('superadmin');
  const status = useTypedSelector(systemStatusSelector);
  const isSystemLoading = useTypedSelector((state) => state.systemStatus.loading);

  if (!Component) return null;

  if (isInitializing || isSystemLoading) {
    return (
      <FullPageLoader>
        <CircularProgress size={60} thickness={4} sx={{ color: '#0057ff' }} />
      </FullPageLoader>
    );
  }

  if (status?.maintenanceMode && !isAdmin && !excludeMaintenance) {
    return <MaintenancePage />;
  }

  if (restricted && (getCookie('accessToken') || isUserLoggedIn)) {
    return <Redirect to={{ pathname: isPublicRoute ? '/public/procedures' : '/' }} />;
  }

  if (authenticated && !getCookie('accessToken') && !isUserLoggedIn) {
    return (
      <Redirect
        to={{
          pathname: isPublicRoute ? '/public/procedures/login' : '/login',
          state: { from: location },
        }}
      />
    );
  }

  const isClient = userRoles.includes('cliente');
  if (verified && isUserLoggedIn && !isUserVerified && isClient) {
    return <Redirect to={{ pathname: '/public/procedures' }} />;
  }
  if (verified && !isUserLoggedIn) {
    return <Redirect to={{ pathname: '/public/procedures/login', state: { from: location } }} />;
  }

  if (
    isUserLoggedIn &&
    roles.length > 0 &&
    !userRoles.some((userRole) => roles.includes(userRole))
  ) {
    return (
      <Redirect
        to={{
          pathname: userRoles.includes(ROLES.cliente) ? '/public/procedures' : '/',
        }}
      />
    );
  }

  return <Route {...rest} render={(props) => <Component {...props} />} />;
};

export default RouteContainer;
